import "./header.scss";

import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import SvgIcon from "@client/components/reusable/SvgIcons";
import { ReactComponent as ActiveOrdersIcon } from "@client/components/reusable/SvgIcons/library/active_orders_icon.svg";
import { ReactComponent as CloseIcon } from "@client/components/reusable/SvgIcons/library/close_icon.svg";
import { ReactComponent as GreenCircle } from "@client/components/reusable/SvgIcons/library/green_circle.svg";
import { ReactComponent as MenuIcon } from "@client/components/reusable/SvgIcons/library/menu_icon.svg";
import { ReactComponent as OrderHistoryIcon } from "@client/components/reusable/SvgIcons/library/order_history_icon.svg";
import { ReactComponent as QuestionCircle } from "@client/components/reusable/SvgIcons/library/question_circle.svg";
import { ReactComponent as RedCircle } from "@client/components/reusable/SvgIcons/library/red_circle.svg";
import { ReactComponent as SignOut } from "@client/components/reusable/SvgIcons/library/SignOut.svg";
import { useRootContext } from "@client/context/RootContext";
import { useNotificationPopup } from "@client/hooks/useNotificationPopup";
import { StatsDashboardIcon } from "@client/pages/stats-dashboard/stats-dashboard";
import { CONFIG_KEYS } from "@client/stores/config-store/config-store";

import Feedback from "../feedback/feedback";
import BtnNotificationPopup from "../notification-popup/btn-notification";
import NotificationStrip from "../notification-popup/notification-strip";
import useClickCount from "../reusable/hooks/useClickCount";
import ShippingSignatureButton from "../shipping-signature-button/shipping-signature-button";
import HeaderSearch from "./header-search";
import { SlowConnection } from "./slow-connection";

export const Header = observer(() => {
	const {
		vendorStore: { vendorDetails },
	} = useRootContext();

	if (!vendorDetails) {
		return null;
	} else {
		return <HeaderComponent />;
	}
});

function getMenuOptions() {
	const {
		layoutStore: { isDashboardActive },
		cookieService,
	} = useRootContext();

	const menuOptions = [
		{
			id: 0,
			title: "הזמנות פעילות", // "Active orders"
			icon: { component: ActiveOrdersIcon, width: "24", height: "32" },
			path: "/",
		},

		{
			id: 2,
			title: "היסטוריית הזמנות", // "Orders history"
			icon: { component: OrderHistoryIcon, width: "24", height: "22" },
			path: "/orders-history",
		},
		{
			id: 3,
			title: "עזרה", // "Help"
			icon: { component: QuestionCircle, width: "24", height: "32" },
			path: "/help",
		},

		{
			id: 4,
			title: "התנתקות", // "Sign out"
			icon: { component: SignOut, width: "24", height: "32" },
			action: () => {
				cookieService.removeAllCookies();
				window.location.reload();
			},
		},
	];

	if (isDashboardActive) {
		menuOptions.push({
			id: 1, // reserved for dashboard page
			path: "/dashboard",
			title: "נתונים",
			icon: { component: StatsDashboardIcon, width: "24", height: "32" },
		});
	}

	menuOptions.sort((a, b) => a.id - b.id);
	return menuOptions;
}

const HeaderComponent = observer(() => {
	const {
		authStore,
		vendorStore: { isStoreOpen, vendorDetails },
		configStore,
		layoutStore,
	} = useRootContext();

	const { isDashboardActive } = layoutStore;

	const [isOpen, setIsOpen] = useState(false);
	const [showNotification, setShowNotification] = useState(false);

	/** click 4 times to log out */
	const incrementCount = useClickCount(authStore.logout);

	// Use notification hook
	const {
		showNotificationButton,
		title,
		message,
		title_url,
		message_url,
		title_config,
		message_config,
		enabled_config,
	} = useNotificationPopup();

	const menuOptions = getMenuOptions();

	const { pathname } = useLocation();

	const isActiveOrdersPath = pathname === menuOptions[0].path;

	const [selectedOption, setSelectedOption] = useState(
		menuOptions.find((option) => option.path == pathname).id
	);

	function toggleNotification() {
		setShowNotification((prev) => !prev);
	}

	function closeNotification() {
		setShowNotification(false);
	}

	// Auto-show notification when flag is set and content has changed
	useEffect(() => {
		// Always show if URL params are present
		if (title_url || message_url) {
			if (title_url && message_url) {
				setShowNotification(true);
			}
			return;
		}

		// For config-based notifications, check if content changed
		if (enabled_config && title_config && message_config) {
			const hasChanged = layoutStore.hasNotificationContentChanged(
				title_config,
				message_config
			);

			if (hasChanged) {
				setShowNotification(true);
			}
		}
	}, [enabled_config, title_config, message_config, title_url, message_url]);

	function triggerMenuOpening() {
		setIsOpen((prevValue) => !prevValue);
	}

	function handleMenuOptionClick(id: number) {
		triggerMenuOpening();
	}

	const menuVariants = {
		open: { opacity: 1, x: 0 },
		closed: { opacity: 0, x: "-100%" },
	};

	useEffect(() => {
		const id = menuOptions.find((option) => option.path == pathname).id;
		setSelectedOption(id);
	}, [pathname]);

	return (
		<>
			<AnimatePresence>
				{showNotification && showNotificationButton && (
					<NotificationStrip
						title={title}
						message={message}
						onClose={closeNotification}
					/>
				)}
			</AnimatePresence>

			<div className="header-container">
				{/* #region menu overlay */}
				{isOpen && (
					<div
						onClick={() => setIsOpen(false)}
						className={classNames("menu-overlay")}
					/>
				)}
				<motion.div
					className="menu-wrapper"
					animate={isOpen ? "open" : "closed"}
					variants={menuVariants}
					exit={{ opacity: 0, x: "-100%" }}
					transition={{ bounce: 0 }}
				>
					<SvgIcon
						component={CloseIcon}
						className="menu-close-btn"
						width="16"
						height="16"
						onClick={triggerMenuOpening}
					/>
					<div
						style={{
							opacity: 0.8,
							marginBottom: 20,
							paddingBottom: 25,
							borderBottom: "0.1px solid white",
							fontSize: 22,
							fontWeight: 500,
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						{vendorDetails.identifier}
					</div>
					{menuOptions.map((option) => (
						<Link
							key={option.title}
							to={option.path}
							onMouseDown={option.action}
							className={classNames("menu-option", {
								["selected"]: selectedOption === option.id,
							})}
							onClick={() => handleMenuOptionClick(option.id)}
						>
							<SvgIcon
								component={option.icon.component}
								width={option.icon.width}
								height={option.icon.height}
								style={{ stroke: "white" }}
							/>
							<div className="menu-option-title">{option.title}</div>
						</Link>
					))}
				</motion.div>
				{/* #endregion */}

				<div className="menu-selector">
					<SvgIcon
						component={MenuIcon}
						onClick={triggerMenuOpening}
						className="menu-icon"
					/>
					<div className="menu-title">{menuOptions[selectedOption].title}</div>
				</div>

				<img
					className={classNames("terminalx-img", {
						// ["shifted"]: isSearchInputFocused || searchTerm,
					})}
					src="/images/terminalx_img.png"
				/>

				<div className="menu-right">
					{isActiveOrdersPath && (
						<>
							<HeaderSearch />

							{isDashboardActive && (
								<Link key={"dashboard-top"} to={"/dashboard"}>
									<StatsDashboardIcon />
								</Link>
							)}

							{/* <BtnPickups /> */}

							{!vendorDetails.is_selfshipment &&
								vendorDetails.show_handshake_dialog && (
									<ShippingSignatureButton />
								)}
						</>
					)}

					{configStore.getConfigValue(CONFIG_KEYS.VENDOR_REPORT_EMAILS) ===
						"1" && <Feedback />}

					{showNotificationButton && (
						<BtnNotificationPopup onClick={toggleNotification} />
					)}

					<div className="store-btn" onClick={incrementCount}>
						{isStoreOpen ? (
							<SvgIcon component={GreenCircle} className="circle-icon" />
						) : (
							<SvgIcon component={RedCircle} className="circle-icon" />
						)}
						<div className="store-btn-text">{`Store ${
							isStoreOpen ? "open" : "closed"
						}`}</div>
					</div>
				</div>

				<SlowConnection />
			</div>
		</>
	);
});
